import React, { useState } from "react";
import { Table, Title, Field, Control, Button, Content } from "rbx";
import { format } from "date-fns";
import PropTypes from "prop-types";

import { useMutation } from "react-apollo";
import HorizontalRule from "../../../../../styles/HorizontalRule";
import { ReactTable } from "../../../../../components/DataTable/DataTable";

import {
  SINGLE_ORDER_QUERY,
  SYNC_ROUTE_ORDER_MUTATION
} from "../../../../../graphql/Orders/Orders.graphql";
import {
  PRODUCT_DETAIL_BILLING_COLUMNS,
  ORDER_DETAIL_BILLING_COLUMNS
} from "./columns";

import { ALL_ORDER_DETAILS_QUERY } from "../OrderDetails";

import "./SummaryRouteRefreshModal.scss";

const SummaryRouteRefreshModal = ({
  order,
  route,
  orderDetails,
  routeProducts,
  onComplete,
  onCancel
}) => {
  const [saving, setSaving] = useState(false);

  const [updateOrder, { loading: loadingUpdate }] = useMutation(
    SYNC_ROUTE_ORDER_MUTATION,
    {
      refetchQueries: [
        {
          query: SINGLE_ORDER_QUERY,
          variables: {
            where: { RecId: order.RecId }
          }
        },
        {
          query: ALL_ORDER_DETAILS_QUERY,
          variables: {
            where: { OrderRecIdRef: order.RecId, ProdCatgRecIdRef: 306 }
          }
        }
      ]
    }
  );

  const handleComplete = async () => {
    setSaving(true);
    await updateOrder({
      variables: {
        where: { RecId: order.RecId },
        data: { RouteRecIdRef: route.RecId }
      }
    });
    await onComplete();
    setSaving(false);
  };

  const fieldsToCompare = [
    "Start Location",
    "Pickup Location",
    "Delivery Location",
    "Ending Location"
  ];

  const displayFn = Location =>
    `${Location.LocationID ? `${Location.LocationID} - ` : ""}${
      Location.LocationName ? `${Location.LocationName} - ` : ""
    }${
      Location.City && Location.State
        ? `${Location.City}, ${Location.State}`
        : ""
    }${Location.TimeZone ? ` - ${Location.TimeZone.CodeId}` : ""}`;

  const getRowData = (field, type) =>
    ({
      "Start Location":
        type === "order"
          ? displayFn(order.StartLocation)
          : displayFn(route.StartLocation),
      "Pickup Location":
        type === "order"
          ? displayFn(order.PickupLocation)
          : displayFn(route.PickupLocation),
      "Delivery Location":
        type === "order"
          ? displayFn(order.DeliveryLocation)
          : displayFn(route.DeliveryLocation),
      "Ending Location":
        type === "order"
          ? displayFn(order.EndingLocation)
          : displayFn(route.EndingLocation)
    }[field]);

  const hasPickupDriver = !!order.PickupDriverRecIdRef;
  const hasDeliveryDriver = !!order.DeliveryDriverRecIdRef;

  const locationsDiffer = [
    "StartLocationRecIdRef",
    "PickupLocationRecIdRef",
    "DeliveryLocationRecIdRef",
    "EndingLocationRecIdRef"
  ].some(field => order[field] !== route[field]);

  const isDisabled = (hasPickupDriver || hasDeliveryDriver) && locationsDiffer;

  const displayMessage = isDisabled ? (
    <p>
      Route locations differ and cannot change to this Route.
      <br />
      Please first go to Order Dispatching for Start Date{" "}
      <b>{format(new Date(order.StartDate), "MM/dd/yyyy")}</b> and clear this
      driver from the Order. Then reassign the Route on the Order.
    </p>
  ) : (
    <p>OK to change to this new Route (Y/N)?</p>
  );

  console.log(orderDetails);
  return (
    <div className="billing-route-compare-container">
      <Title color="primary" size={3}>
        Sync Route
      </Title>
      <Table bordered fullwidth hoverable narrow>
        <Table.Head>
          <Table.Row>
            <Table.Heading className="header"></Table.Heading>
            <Table.Heading className="header">
              Order #{order.OrderNumber}
            </Table.Heading>
            <Table.Heading className="header">
              Route {route.RouteDescription}
            </Table.Heading>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {fieldsToCompare.map(field => {
            const orderData = getRowData(field, "order");
            const routeData = getRowData(field, "route");
            return (
              <Table.Row key={field}>
                <Table.Cell className="field-name">{field}</Table.Cell>
                <Table.Cell
                  className={orderData !== routeData ? "different" : "equal"}
                >
                  {orderData || "--"}
                </Table.Cell>
                <Table.Cell
                  className={orderData !== routeData ? "different" : "equal"}
                >
                  {routeData || "--"}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      <div className="products-container">
        <div>
          <Title color="primary" size={6}>
            Order Details
          </Title>
          <ReactTable
            columns={ORDER_DETAIL_BILLING_COLUMNS}
            data={orderDetails}
            initialSortBy={[
              { id: "ProductCategoryDescription", desc: false },
              { id: "ProductProductCode", desc: false },
              { id: "Description", desc: false }
            ]}
          />
        </div>
        <div>
          <Title color="primary" size={6}>
            Route Products
          </Title>
          <ReactTable
            columns={PRODUCT_DETAIL_BILLING_COLUMNS(orderDetails)}
            data={routeProducts}
            initialSortBy={[
              { id: "ProductCategoryDescription", desc: false },
              { id: "ProductProductCode", desc: false },
              { id: "ProdDescription", desc: false }
            ]}
          />
        </div>
      </div>
      <HorizontalRule />

      <Content>{displayMessage}</Content>

      <Field kind="group">
        <Control>
          <Button
            color="success"
            disabled={isDisabled || loadingUpdate || saving}
            state={loadingUpdate || saving ? "loading" : ""}
            onClick={handleComplete}
          >
            Yes
          </Button>
        </Control>
        <Control>
          <Button
            color="primary"
            disabled={loadingUpdate || saving}
            state={loadingUpdate || saving ? "loading" : ""}
            onClick={onCancel}
          >
            No
          </Button>
        </Control>
      </Field>
    </div>
  );
};

SummaryRouteRefreshModal.propTypes = {
  order: PropTypes.object,
  route: PropTypes.object,
  orderDetails: PropTypes.func,
  routeProducts: PropTypes.func,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
};

export default SummaryRouteRefreshModal;
